/**
 * Code templates for custom (Python sandbox) model creation.
 *
 * Each template provides a ready-to-run recipe that follows the standard
 * contract:
 *   Inputs:  dataset_records, training_config, runtime_spec, metadata
 *   Output:  result dict with model artefacts + metrics
 *
 * Templates are grouped by family (ML / DL / RL) and within each family
 * by category (e.g. classification, regression, time-series …).
 */

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface CodeTemplate {
  id: string;
  label: string;
  family: 'ML' | 'DL' | 'RL';
  category: string;
  framework: string;          // maps to ModelLabFramework
  problemType: string;        // maps to ModelLabProblemType
  description: string;        // one-liner shown in selector
  libraries: string;          // comma-separated allowlist
  entrypoint: string;
  code: string;
}

export interface TemplateCategory {
  label: string;
  templates: CodeTemplate[];
}

export interface TemplateFamily {
  family: 'ML' | 'DL' | 'RL';
  label: string;
  categories: TemplateCategory[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------
const indent = (text: string) => text.replace(/^ {4}/gm, '');

// ---------------------------------------------------------------------------
// 1. MACHINE LEARNING TEMPLATES
// ---------------------------------------------------------------------------

const ML_CLASSIFICATION: CodeTemplate[] = [
  {
    id: 'ml_random_forest_clf',
    label: 'Random Forest Classifier',
    family: 'ML',
    category: 'Classification',
    framework: 'SKLEARN',
    problemType: 'CLASSIFICATION',
    description: 'Ensemble of decision trees with bootstrap aggregation',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Random Forest Classifier
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, f1_score, classification_report
from sklearn.preprocessing import StandardScaler, LabelEncoder

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    le = LabelEncoder()
    y = le.fit_transform(df[target].values)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42, stratify=y
    )

    model = RandomForestClassifier(
        n_estimators=training_config.get('n_estimators', 200),
        max_depth=training_config.get('max_depth', None),
        min_samples_split=training_config.get('min_samples_split', 2),
        random_state=42,
        n_jobs=-1,
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_test, y_pred)),
            'f1_weighted': float(f1_score(y_test, y_pred, average='weighted')),
        },
        'feature_importances': dict(zip(feature_cols, model.feature_importances_.tolist())),
        'classification_report': classification_report(y_test, y_pred, output_dict=True),
    }
    return result
`,
  },
  {
    id: 'ml_xgboost_clf',
    label: 'XGBoost Classifier',
    family: 'ML',
    category: 'Classification',
    framework: 'SKLEARN',
    problemType: 'CLASSIFICATION',
    description: 'Gradient-boosted trees optimised for tabular data',
    libraries: 'numpy, pandas, sklearn, xgboost',
    entrypoint: 'train_model',
    code: `# XGBoost Classifier
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from xgboost import XGBClassifier
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, f1_score, log_loss
from sklearn.preprocessing import StandardScaler, LabelEncoder

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    le = LabelEncoder()
    y = le.fit_transform(df[target].values)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42, stratify=y
    )

    model = XGBClassifier(
        n_estimators=training_config.get('n_estimators', 300),
        max_depth=training_config.get('max_depth', 6),
        learning_rate=training_config.get('learning_rate', 0.1),
        subsample=0.8,
        colsample_bytree=0.8,
        use_label_encoder=False,
        eval_metric='mlogloss',
        random_state=42,
    )
    model.fit(X_train, y_train, eval_set=[(X_test, y_test)], verbose=False)

    y_pred = model.predict(X_test)
    y_proba = model.predict_proba(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_test, y_pred)),
            'f1_weighted': float(f1_score(y_test, y_pred, average='weighted')),
            'log_loss': float(log_loss(y_test, y_proba)),
        },
        'feature_importances': dict(zip(feature_cols, model.feature_importances_.tolist())),
    }
    return result
`,
  },
  {
    id: 'ml_svm_clf',
    label: 'SVM Classifier',
    family: 'ML',
    category: 'Classification',
    framework: 'SKLEARN',
    problemType: 'CLASSIFICATION',
    description: 'Support Vector Machine with RBF kernel for non-linear boundaries',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# SVM Classifier
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.svm import SVC
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, f1_score
from sklearn.preprocessing import StandardScaler, LabelEncoder

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    le = LabelEncoder()
    y = le.fit_transform(df[target].values)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42, stratify=y
    )

    model = SVC(
        C=training_config.get('C', 1.0),
        kernel=training_config.get('kernel', 'rbf'),
        gamma=training_config.get('gamma', 'scale'),
        probability=True,
        random_state=42,
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_test, y_pred)),
            'f1_weighted': float(f1_score(y_test, y_pred, average='weighted')),
        },
    }
    return result
`,
  },
  {
    id: 'ml_logistic_clf',
    label: 'Logistic Regression',
    family: 'ML',
    category: 'Classification',
    framework: 'SKLEARN',
    problemType: 'CLASSIFICATION',
    description: 'Fast linear model for binary and multi-class classification',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Logistic Regression Classifier
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.linear_model import LogisticRegression
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, f1_score, roc_auc_score
from sklearn.preprocessing import StandardScaler, LabelEncoder

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    le = LabelEncoder()
    y = le.fit_transform(df[target].values)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42, stratify=y
    )

    model = LogisticRegression(
        C=training_config.get('C', 1.0),
        max_iter=training_config.get('max_iter', 1000),
        solver='lbfgs',
        multi_class='auto',
        random_state=42,
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    y_proba = model.predict_proba(X_test)
    metrics: dict = {
        'accuracy': float(accuracy_score(y_test, y_pred)),
        'f1_weighted': float(f1_score(y_test, y_pred, average='weighted')),
    }
    if len(le.classes_) == 2:
        metrics['roc_auc'] = float(roc_auc_score(y_test, y_proba[:, 1]))

    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': metrics,
        'coefficients': dict(zip(feature_cols, model.coef_[0].tolist())),
    }
    return result
`,
  },
];

const ML_REGRESSION: CodeTemplate[] = [
  {
    id: 'ml_gradient_boost_reg',
    label: 'Gradient Boosting Regressor',
    family: 'ML',
    category: 'Regression',
    framework: 'SKLEARN',
    problemType: 'REGRESSION',
    description: 'Stage-wise additive boosting for continuous targets',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Gradient Boosting Regressor
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.ensemble import GradientBoostingRegressor
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    y = df[target].values.astype(float)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42
    )

    model = GradientBoostingRegressor(
        n_estimators=training_config.get('n_estimators', 300),
        max_depth=training_config.get('max_depth', 5),
        learning_rate=training_config.get('learning_rate', 0.1),
        subsample=0.8,
        random_state=42,
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_test, y_pred))),
            'mae': float(mean_absolute_error(y_test, y_pred)),
            'r2': float(r2_score(y_test, y_pred)),
        },
        'feature_importances': dict(zip(feature_cols, model.feature_importances_.tolist())),
    }
    return result
`,
  },
  {
    id: 'ml_ridge_reg',
    label: 'Ridge Regression',
    family: 'ML',
    category: 'Regression',
    framework: 'SKLEARN',
    problemType: 'REGRESSION',
    description: 'L2-regularised linear regression — fast baseline for continuous targets',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Ridge Regression
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.linear_model import Ridge
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    y = df[target].values.astype(float)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42
    )

    model = Ridge(
        alpha=training_config.get('alpha', 1.0),
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_test, y_pred))),
            'mae': float(mean_absolute_error(y_test, y_pred)),
            'r2': float(r2_score(y_test, y_pred)),
        },
        'coefficients': dict(zip(feature_cols, model.coef_.tolist())),
    }
    return result
`,
  },
  {
    id: 'ml_elasticnet_reg',
    label: 'ElasticNet Regression',
    family: 'ML',
    category: 'Regression',
    framework: 'SKLEARN',
    problemType: 'REGRESSION',
    description: 'Combined L1 + L2 regularization for sparse feature selection',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# ElasticNet Regression
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.linear_model import ElasticNet
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values
    y = df[target].values.astype(float)

    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    X_train, X_test, y_train, y_test = train_test_split(
        X, y, test_size=0.2, random_state=42
    )

    model = ElasticNet(
        alpha=training_config.get('alpha', 1.0),
        l1_ratio=training_config.get('l1_ratio', 0.5),
        max_iter=2000,
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_test, y_pred))),
            'mae': float(mean_absolute_error(y_test, y_pred)),
            'r2': float(r2_score(y_test, y_pred)),
        },
        'coefficients': dict(zip(feature_cols, model.coef_.tolist())),
    }
    return result
`,
  },
];

const ML_TIMESERIES: CodeTemplate[] = [
  {
    id: 'ml_price_forecast',
    label: 'Price Forecast (OHLCV)',
    family: 'ML',
    category: 'Time-Series',
    framework: 'SKLEARN',
    problemType: 'REGRESSION',
    description: 'Lag-based features on OHLCV bars for next-bar price prediction',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Price Forecast — Lag-based OHLCV Regression
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.ensemble import GradientBoostingRegressor
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    lookback = training_config.get('lookback', 10)
    target_col = training_config.get('target_column', 'close')

    # Build lag features
    for lag in range(1, lookback + 1):
        for col in ['open', 'high', 'low', 'close', 'volume']:
            if col in df.columns:
                df[f'{col}_lag{lag}'] = df[col].shift(lag)

    # Returns
    df['return_1'] = df['close'].pct_change()
    df['return_5'] = df['close'].pct_change(5)

    # Target: next-bar close
    df['target'] = df[target_col].shift(-1)
    df = df.dropna()

    feature_cols = [c for c in df.columns if c not in ['timestamp', 'target', target_col]]
    X = df[feature_cols].values
    y = df['target'].values

    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    scaler = StandardScaler()
    X_train = scaler.fit_transform(X_train)
    X_test = scaler.transform(X_test)

    model = GradientBoostingRegressor(
        n_estimators=200, max_depth=5, learning_rate=0.05, random_state=42
    )
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_test, y_pred))),
            'mae': float(mean_absolute_error(y_test, y_pred)),
            'r2': float(r2_score(y_test, y_pred)),
        },
        'feature_importances': dict(zip(feature_cols, model.feature_importances_.tolist())),
        'lookback': lookback,
    }
    return result
`,
  },
  {
    id: 'ml_signal_clf',
    label: 'Trading Signal Classifier',
    family: 'ML',
    category: 'Time-Series',
    framework: 'SKLEARN',
    problemType: 'CLASSIFICATION',
    description: 'Technical-indicator features to predict buy / hold / sell signal',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Trading Signal Classifier
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.ensemble import RandomForestClassifier
from sklearn.model_selection import TimeSeriesSplit
from sklearn.metrics import accuracy_score, f1_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    threshold = training_config.get('signal_threshold', 0.001)

    # Feature engineering
    df['return'] = df['close'].pct_change()
    df['sma_10'] = df['close'].rolling(10).mean()
    df['sma_30'] = df['close'].rolling(30).mean()
    df['rsi'] = _rsi(df['close'], 14)
    df['vol_ratio'] = df['volume'] / df['volume'].rolling(20).mean()

    # Target: next-bar signal  (1=buy, 0=hold, -1=sell)
    next_ret = df['close'].pct_change().shift(-1)
    df['signal'] = 0
    df.loc[next_ret > threshold, 'signal'] = 1
    df.loc[next_ret < -threshold, 'signal'] = -1
    df = df.dropna()

    feature_cols = ['return', 'sma_10', 'sma_30', 'rsi', 'vol_ratio']
    X = df[feature_cols].values
    y = df['signal'].values

    # Time-series aware split
    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    scaler = StandardScaler()
    X_train = scaler.fit_transform(X_train)
    X_test = scaler.transform(X_test)

    model = RandomForestClassifier(n_estimators=300, max_depth=8, random_state=42, n_jobs=-1)
    model.fit(X_train, y_train)

    y_pred = model.predict(X_test)
    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'accuracy': float(accuracy_score(y_test, y_pred)),
            'f1_weighted': float(f1_score(y_test, y_pred, average='weighted')),
        },
        'feature_importances': dict(zip(feature_cols, model.feature_importances_.tolist())),
    }
    return result

def _rsi(series, period=14):
    delta = series.diff()
    gain = delta.clip(lower=0).rolling(period).mean()
    loss = (-delta.clip(upper=0)).rolling(period).mean()
    rs = gain / loss.replace(0, np.nan)
    return 100 - (100 / (1 + rs))
`,
  },
];

const ML_CLUSTERING: CodeTemplate[] = [
  {
    id: 'ml_kmeans_cluster',
    label: 'K-Means Clustering',
    family: 'ML',
    category: 'Clustering',
    framework: 'SKLEARN',
    problemType: 'OTHER',
    description: 'Partition data into k clusters by centroid distance',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# K-Means Clustering
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.cluster import KMeans
from sklearn.metrics import silhouette_score, calinski_harabasz_score
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.select_dtypes(include=[np.number]).columns]

    X = df[feature_cols].dropna().values
    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    k = training_config.get('n_clusters', 5)
    model = KMeans(n_clusters=k, n_init=10, random_state=42)
    labels = model.fit_predict(X)

    result = {
        'model': model,
        'scaler': scaler,
        'labels': labels.tolist(),
        'metrics': {
            'inertia': float(model.inertia_),
            'silhouette': float(silhouette_score(X, labels)),
            'calinski_harabasz': float(calinski_harabasz_score(X, labels)),
        },
        'cluster_centers': model.cluster_centers_.tolist(),
    }
    return result
`,
  },
];

const ML_ANOMALY: CodeTemplate[] = [
  {
    id: 'ml_isolation_forest',
    label: 'Isolation Forest (Anomaly)',
    family: 'ML',
    category: 'Anomaly Detection',
    framework: 'SKLEARN',
    problemType: 'OTHER',
    description: 'Tree-based anomaly detection for outlier / fraud identification',
    libraries: 'numpy, pandas, sklearn',
    entrypoint: 'train_model',
    code: `# Isolation Forest — Anomaly Detection
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
from sklearn.ensemble import IsolationForest
from sklearn.preprocessing import StandardScaler

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.select_dtypes(include=[np.number]).columns]

    X = df[feature_cols].dropna().values
    scaler = StandardScaler()
    X = scaler.fit_transform(X)

    contamination = training_config.get('contamination', 0.05)
    model = IsolationForest(
        n_estimators=200,
        contamination=contamination,
        random_state=42,
        n_jobs=-1,
    )
    model.fit(X)

    scores = model.decision_function(X)
    labels = model.predict(X)
    n_anomalies = int((labels == -1).sum())

    result = {
        'model': model,
        'scaler': scaler,
        'anomaly_labels': labels.tolist(),
        'anomaly_scores': scores.tolist(),
        'metrics': {
            'n_anomalies': n_anomalies,
            'anomaly_rate': round(n_anomalies / len(X), 4),
        },
    }
    return result
`,
  },
];

// ---------------------------------------------------------------------------
// 2. DEEP LEARNING TEMPLATES
// ---------------------------------------------------------------------------

const DL_FEEDFORWARD: CodeTemplate[] = [
  {
    id: 'dl_mlp_clf',
    label: 'MLP Classifier',
    family: 'DL',
    category: 'Feedforward',
    framework: 'PYTORCH',
    problemType: 'CLASSIFICATION',
    description: 'Multi-layer perceptron for tabular classification',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# MLP Classifier — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.model_selection import train_test_split
from sklearn.metrics import accuracy_score, f1_score
from sklearn.preprocessing import StandardScaler, LabelEncoder

class MLPClassifier(nn.Module):
    def __init__(self, input_dim, hidden_dims, n_classes, dropout=0.3):
        super().__init__()
        layers = []
        prev = input_dim
        for h in hidden_dims:
            layers += [nn.Linear(prev, h), nn.BatchNorm1d(h), nn.ReLU(), nn.Dropout(dropout)]
            prev = h
        layers.append(nn.Linear(prev, n_classes))
        self.net = nn.Sequential(*layers)

    def forward(self, x):
        return self.net(x)

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values.astype(np.float32)
    le = LabelEncoder()
    y = le.fit_transform(df[target].values)

    scaler = StandardScaler()
    X = scaler.fit_transform(X).astype(np.float32)

    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42, stratify=y)

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_ds = TensorDataset(torch.tensor(X_train), torch.tensor(y_train, dtype=torch.long))
    test_ds = TensorDataset(torch.tensor(X_test), torch.tensor(y_test, dtype=torch.long))
    train_dl = DataLoader(train_ds, batch_size=training_config.get('batch_size', 64), shuffle=True)
    test_dl = DataLoader(test_ds, batch_size=256)

    hidden = training_config.get('hidden_dims', [128, 64])
    model = MLPClassifier(X_train.shape[1], hidden, len(le.classes_)).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.CrossEntropyLoss()
    epochs = training_config.get('epochs', 50)

    for epoch in range(epochs):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).argmax(1).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_true, y_pred)),
            'f1_weighted': float(f1_score(y_true, y_pred, average='weighted')),
        },
    }
    return result
`,
  },
  {
    id: 'dl_mlp_reg',
    label: 'MLP Regressor',
    family: 'DL',
    category: 'Feedforward',
    framework: 'PYTORCH',
    problemType: 'REGRESSION',
    description: 'Multi-layer perceptron for continuous value prediction',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# MLP Regressor — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.model_selection import train_test_split
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

class MLPRegressor(nn.Module):
    def __init__(self, input_dim, hidden_dims, dropout=0.2):
        super().__init__()
        layers = []
        prev = input_dim
        for h in hidden_dims:
            layers += [nn.Linear(prev, h), nn.BatchNorm1d(h), nn.ReLU(), nn.Dropout(dropout)]
            prev = h
        layers.append(nn.Linear(prev, 1))
        self.net = nn.Sequential(*layers)

    def forward(self, x):
        return self.net(x).squeeze(-1)

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    target = training_config.get('target_column', 'target')
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.columns if c != target]

    X = df[feature_cols].values.astype(np.float32)
    y = df[target].values.astype(np.float32)

    scaler = StandardScaler()
    X = scaler.fit_transform(X).astype(np.float32)

    X_train, X_test, y_train, y_test = train_test_split(X, y, test_size=0.2, random_state=42)

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_ds = TensorDataset(torch.tensor(X_train), torch.tensor(y_train))
    test_ds = TensorDataset(torch.tensor(X_test), torch.tensor(y_test))
    train_dl = DataLoader(train_ds, batch_size=training_config.get('batch_size', 64), shuffle=True)
    test_dl = DataLoader(test_ds, batch_size=256)

    hidden = training_config.get('hidden_dims', [128, 64])
    model = MLPRegressor(X_train.shape[1], hidden).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.MSELoss()
    epochs = training_config.get('epochs', 50)

    for epoch in range(epochs):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_true, y_pred))),
            'mae': float(mean_absolute_error(y_true, y_pred)),
            'r2': float(r2_score(y_true, y_pred)),
        },
    }
    return result
`,
  },
];

const DL_SEQUENCE: CodeTemplate[] = [
  {
    id: 'dl_lstm_forecast',
    label: 'LSTM Price Forecast',
    family: 'DL',
    category: 'Sequence Models',
    framework: 'PYTORCH',
    problemType: 'REGRESSION',
    description: 'LSTM network for time-series price forecasting',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# LSTM Price Forecast — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler

class LSTMForecast(nn.Module):
    def __init__(self, input_dim, hidden_dim=64, num_layers=2, dropout=0.2):
        super().__init__()
        self.lstm = nn.LSTM(input_dim, hidden_dim, num_layers, batch_first=True, dropout=dropout)
        self.head = nn.Linear(hidden_dim, 1)

    def forward(self, x):
        out, _ = self.lstm(x)
        return self.head(out[:, -1, :]).squeeze(-1)

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    seq_len = training_config.get('seq_len', 20)
    target_col = training_config.get('target_column', 'close')
    feature_cols = [c for c in ['open', 'high', 'low', 'close', 'volume'] if c in df.columns]

    scaler = StandardScaler()
    data = scaler.fit_transform(df[feature_cols].values).astype(np.float32)
    target_idx = feature_cols.index(target_col)

    # Build sequences
    X_seqs, y_seqs = [], []
    for i in range(len(data) - seq_len):
        X_seqs.append(data[i:i + seq_len])
        y_seqs.append(data[i + seq_len, target_idx])
    X = np.array(X_seqs)
    y = np.array(y_seqs)

    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_dl = DataLoader(
        TensorDataset(torch.tensor(X_train), torch.tensor(y_train)),
        batch_size=training_config.get('batch_size', 32), shuffle=True,
    )
    test_dl = DataLoader(
        TensorDataset(torch.tensor(X_test), torch.tensor(y_test)),
        batch_size=256,
    )

    model = LSTMForecast(
        input_dim=len(feature_cols),
        hidden_dim=training_config.get('hidden_dim', 64),
        num_layers=training_config.get('num_layers', 2),
    ).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.MSELoss()

    for epoch in range(training_config.get('epochs', 30)):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_true, y_pred))),
            'mae': float(mean_absolute_error(y_true, y_pred)),
            'r2': float(r2_score(y_true, y_pred)),
        },
        'seq_len': seq_len,
    }
    return result
`,
  },
  {
    id: 'dl_gru_clf',
    label: 'GRU Sequence Classifier',
    family: 'DL',
    category: 'Sequence Models',
    framework: 'PYTORCH',
    problemType: 'CLASSIFICATION',
    description: 'GRU-based classifier for sequential / temporal classification',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# GRU Sequence Classifier — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.metrics import accuracy_score, f1_score
from sklearn.preprocessing import StandardScaler, LabelEncoder

class GRUClassifier(nn.Module):
    def __init__(self, input_dim, hidden_dim=64, num_layers=2, n_classes=2, dropout=0.2):
        super().__init__()
        self.gru = nn.GRU(input_dim, hidden_dim, num_layers, batch_first=True, dropout=dropout)
        self.head = nn.Linear(hidden_dim, n_classes)

    def forward(self, x):
        out, _ = self.gru(x)
        return self.head(out[:, -1, :])

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    seq_len = training_config.get('seq_len', 20)
    target_col = training_config.get('target_column', 'signal')
    feature_cols = [c for c in ['open', 'high', 'low', 'close', 'volume'] if c in df.columns]

    scaler = StandardScaler()
    data = scaler.fit_transform(df[feature_cols].values).astype(np.float32)
    le = LabelEncoder()
    labels = le.fit_transform(df[target_col].values)

    X_seqs, y_seqs = [], []
    for i in range(len(data) - seq_len):
        X_seqs.append(data[i:i + seq_len])
        y_seqs.append(labels[i + seq_len])
    X = np.array(X_seqs)
    y = np.array(y_seqs)

    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_dl = DataLoader(
        TensorDataset(torch.tensor(X_train), torch.tensor(y_train, dtype=torch.long)),
        batch_size=training_config.get('batch_size', 32), shuffle=True,
    )
    test_dl = DataLoader(
        TensorDataset(torch.tensor(X_test), torch.tensor(y_test, dtype=torch.long)),
        batch_size=256,
    )

    model = GRUClassifier(
        input_dim=len(feature_cols),
        hidden_dim=training_config.get('hidden_dim', 64),
        num_layers=training_config.get('num_layers', 2),
        n_classes=len(le.classes_),
    ).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.CrossEntropyLoss()

    for epoch in range(training_config.get('epochs', 30)):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).argmax(1).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_true, y_pred)),
            'f1_weighted': float(f1_score(y_true, y_pred, average='weighted')),
        },
    }
    return result
`,
  },
  {
    id: 'dl_transformer_forecast',
    label: 'Transformer Price Forecast',
    family: 'DL',
    category: 'Sequence Models',
    framework: 'PYTORCH',
    problemType: 'REGRESSION',
    description: 'Transformer encoder for multi-step time-series forecasting',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# Transformer Price Forecast — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.metrics import mean_squared_error, mean_absolute_error, r2_score
from sklearn.preprocessing import StandardScaler
import math

class PositionalEncoding(nn.Module):
    def __init__(self, d_model, max_len=500):
        super().__init__()
        pe = torch.zeros(max_len, d_model)
        pos = torch.arange(0, max_len).unsqueeze(1).float()
        div = torch.exp(torch.arange(0, d_model, 2).float() * -(math.log(10000.0) / d_model))
        pe[:, 0::2] = torch.sin(pos * div)
        pe[:, 1::2] = torch.cos(pos * div)
        self.register_buffer('pe', pe.unsqueeze(0))

    def forward(self, x):
        return x + self.pe[:, :x.size(1)]

class TransformerForecast(nn.Module):
    def __init__(self, input_dim, d_model=64, nhead=4, num_layers=2, dropout=0.1):
        super().__init__()
        self.proj = nn.Linear(input_dim, d_model)
        self.pe = PositionalEncoding(d_model)
        enc_layer = nn.TransformerEncoderLayer(d_model, nhead, d_model * 4, dropout, batch_first=True)
        self.encoder = nn.TransformerEncoder(enc_layer, num_layers)
        self.head = nn.Linear(d_model, 1)

    def forward(self, x):
        x = self.pe(self.proj(x))
        x = self.encoder(x)
        return self.head(x[:, -1, :]).squeeze(-1)

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    seq_len = training_config.get('seq_len', 30)
    target_col = training_config.get('target_column', 'close')
    feature_cols = [c for c in ['open', 'high', 'low', 'close', 'volume'] if c in df.columns]

    scaler = StandardScaler()
    data = scaler.fit_transform(df[feature_cols].values).astype(np.float32)
    target_idx = feature_cols.index(target_col)

    X_seqs, y_seqs = [], []
    for i in range(len(data) - seq_len):
        X_seqs.append(data[i:i + seq_len])
        y_seqs.append(data[i + seq_len, target_idx])
    X = np.array(X_seqs)
    y = np.array(y_seqs)

    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_dl = DataLoader(
        TensorDataset(torch.tensor(X_train), torch.tensor(y_train)),
        batch_size=training_config.get('batch_size', 32), shuffle=True,
    )
    test_dl = DataLoader(
        TensorDataset(torch.tensor(X_test), torch.tensor(y_test)),
        batch_size=256,
    )

    model = TransformerForecast(
        input_dim=len(feature_cols),
        d_model=training_config.get('d_model', 64),
        nhead=training_config.get('nhead', 4),
        num_layers=training_config.get('num_layers', 2),
    ).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 5e-4))
    loss_fn = nn.MSELoss()

    for epoch in range(training_config.get('epochs', 40)):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'metrics': {
            'rmse': float(np.sqrt(mean_squared_error(y_true, y_pred))),
            'mae': float(mean_absolute_error(y_true, y_pred)),
            'r2': float(r2_score(y_true, y_pred)),
        },
        'seq_len': seq_len,
    }
    return result
`,
  },
];

const DL_CNN: CodeTemplate[] = [
  {
    id: 'dl_conv1d_clf',
    label: '1D-CNN Classifier',
    family: 'DL',
    category: 'Convolutional',
    framework: 'PYTORCH',
    problemType: 'CLASSIFICATION',
    description: '1D convolutions over sequential features for pattern classification',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# 1D-CNN Classifier — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.metrics import accuracy_score, f1_score
from sklearn.preprocessing import StandardScaler, LabelEncoder

class Conv1DClassifier(nn.Module):
    def __init__(self, in_channels, seq_len, n_classes):
        super().__init__()
        self.conv = nn.Sequential(
            nn.Conv1d(in_channels, 64, kernel_size=3, padding=1),
            nn.BatchNorm1d(64), nn.ReLU(), nn.MaxPool1d(2),
            nn.Conv1d(64, 128, kernel_size=3, padding=1),
            nn.BatchNorm1d(128), nn.ReLU(), nn.AdaptiveAvgPool1d(1),
        )
        self.head = nn.Sequential(nn.Flatten(), nn.Linear(128, 64), nn.ReLU(), nn.Linear(64, n_classes))

    def forward(self, x):
        # x: (batch, seq_len, channels) → transpose for Conv1d
        return self.head(self.conv(x.transpose(1, 2)))

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    seq_len = training_config.get('seq_len', 20)
    target_col = training_config.get('target_column', 'signal')
    feature_cols = [c for c in ['open', 'high', 'low', 'close', 'volume'] if c in df.columns]

    scaler = StandardScaler()
    data = scaler.fit_transform(df[feature_cols].values).astype(np.float32)
    le = LabelEncoder()
    labels = le.fit_transform(df[target_col].values)

    X_seqs, y_seqs = [], []
    for i in range(len(data) - seq_len):
        X_seqs.append(data[i:i + seq_len])
        y_seqs.append(labels[i + seq_len])
    X = np.array(X_seqs)
    y = np.array(y_seqs)

    split = int(len(X) * 0.8)
    X_train, X_test = X[:split], X[split:]
    y_train, y_test = y[:split], y[split:]

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    train_dl = DataLoader(
        TensorDataset(torch.tensor(X_train), torch.tensor(y_train, dtype=torch.long)),
        batch_size=training_config.get('batch_size', 32), shuffle=True,
    )
    test_dl = DataLoader(
        TensorDataset(torch.tensor(X_test), torch.tensor(y_test, dtype=torch.long)),
        batch_size=256,
    )

    model = Conv1DClassifier(len(feature_cols), seq_len, len(le.classes_)).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.CrossEntropyLoss()

    for epoch in range(training_config.get('epochs', 30)):
        model.train()
        for xb, yb in train_dl:
            xb, yb = xb.to(device), yb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), yb).backward()
            opt.step()

    model.eval()
    all_preds, all_true = [], []
    with torch.no_grad():
        for xb, yb in test_dl:
            all_preds.append(model(xb.to(device)).argmax(1).cpu())
            all_true.append(yb)
    y_pred = torch.cat(all_preds).numpy()
    y_true = torch.cat(all_true).numpy()

    result = {
        'model': model,
        'scaler': scaler,
        'label_encoder': le,
        'metrics': {
            'accuracy': float(accuracy_score(y_true, y_pred)),
            'f1_weighted': float(f1_score(y_true, y_pred, average='weighted')),
        },
    }
    return result
`,
  },
];

const DL_AUTOENCODER: CodeTemplate[] = [
  {
    id: 'dl_autoencoder_anomaly',
    label: 'Autoencoder Anomaly Detector',
    family: 'DL',
    category: 'Autoencoder',
    framework: 'PYTORCH',
    problemType: 'OTHER',
    description: 'Reconstruction-error based anomaly detection with undercomplete autoencoder',
    libraries: 'numpy, pandas, torch, sklearn',
    entrypoint: 'train_model',
    code: `# Autoencoder Anomaly Detector — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.utils.data import DataLoader, TensorDataset
from sklearn.preprocessing import StandardScaler

class Autoencoder(nn.Module):
    def __init__(self, input_dim, latent_dim=8):
        super().__init__()
        self.encoder = nn.Sequential(
            nn.Linear(input_dim, 64), nn.ReLU(),
            nn.Linear(64, 32), nn.ReLU(),
            nn.Linear(32, latent_dim),
        )
        self.decoder = nn.Sequential(
            nn.Linear(latent_dim, 32), nn.ReLU(),
            nn.Linear(32, 64), nn.ReLU(),
            nn.Linear(64, input_dim),
        )

    def forward(self, x):
        return self.decoder(self.encoder(x))

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records)
    feature_cols = training_config.get('feature_columns', [])
    if not feature_cols:
        feature_cols = [c for c in df.select_dtypes(include=[np.number]).columns]

    X = df[feature_cols].dropna().values.astype(np.float32)
    scaler = StandardScaler()
    X = scaler.fit_transform(X).astype(np.float32)

    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')
    dl = DataLoader(TensorDataset(torch.tensor(X)), batch_size=training_config.get('batch_size', 64), shuffle=True)

    model = Autoencoder(X.shape[1], training_config.get('latent_dim', 8)).to(device)
    opt = torch.optim.Adam(model.parameters(), lr=training_config.get('lr', 1e-3))
    loss_fn = nn.MSELoss()

    for epoch in range(training_config.get('epochs', 50)):
        model.train()
        for (xb,) in dl:
            xb = xb.to(device)
            opt.zero_grad()
            loss_fn(model(xb), xb).backward()
            opt.step()

    model.eval()
    with torch.no_grad():
        recon = model(torch.tensor(X).to(device)).cpu().numpy()
    mse = np.mean((X - recon) ** 2, axis=1)
    threshold = float(np.percentile(mse, training_config.get('anomaly_percentile', 95)))
    anomalies = (mse > threshold).astype(int)

    result = {
        'model': model,
        'scaler': scaler,
        'reconstruction_errors': mse.tolist(),
        'threshold': threshold,
        'anomaly_labels': anomalies.tolist(),
        'metrics': {
            'n_anomalies': int(anomalies.sum()),
            'anomaly_rate': round(float(anomalies.mean()), 4),
            'mean_recon_error': float(mse.mean()),
        },
    }
    return result
`,
  },
];

// ---------------------------------------------------------------------------
// 3. REINFORCEMENT LEARNING TEMPLATES
// ---------------------------------------------------------------------------

const RL_TRADING: CodeTemplate[] = [
  {
    id: 'rl_dqn_trading',
    label: 'DQN Trading Agent',
    family: 'RL',
    category: 'Value-Based',
    framework: 'PYTORCH',
    problemType: 'OTHER',
    description: 'Deep Q-Network for discrete buy / hold / sell market actions',
    libraries: 'numpy, pandas, torch',
    entrypoint: 'train_model',
    code: `# DQN Trading Agent — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
import random
from collections import deque

class QNetwork(nn.Module):
    def __init__(self, state_dim, n_actions=3):
        super().__init__()
        self.net = nn.Sequential(
            nn.Linear(state_dim, 128), nn.ReLU(),
            nn.Linear(128, 64), nn.ReLU(),
            nn.Linear(64, n_actions),
        )
    def forward(self, x):
        return self.net(x)

class TradingEnv:
    """Minimal trading environment: observe window of returns, act buy/hold/sell."""
    ACTIONS = ['sell', 'hold', 'buy']

    def __init__(self, prices, window=20, commission=0.0002):
        self.prices = prices
        self.window = window
        self.commission = commission
        self.reset()

    def reset(self):
        self.idx = self.window
        self.position = 0       # -1, 0, 1
        self.pnl = 0.0
        return self._state()

    def _state(self):
        w = self.prices[self.idx - self.window:self.idx]
        returns = np.diff(w) / w[:-1]
        return np.append(returns, self.position).astype(np.float32)

    def step(self, action):
        # action: 0=sell, 1=hold, 2=buy
        price = self.prices[self.idx]
        new_pos = action - 1  # map to -1, 0, 1
        # PnL from price change while holding
        ret = (self.prices[self.idx] - self.prices[self.idx - 1]) / self.prices[self.idx - 1]
        step_pnl = self.position * ret
        # Commission on position change
        if new_pos != self.position:
            step_pnl -= self.commission
        self.position = new_pos
        self.pnl += step_pnl
        self.idx += 1
        done = self.idx >= len(self.prices) - 1
        return self._state(), step_pnl, done

class ReplayBuffer:
    def __init__(self, capacity=10000):
        self.buf = deque(maxlen=capacity)
    def push(self, *args):
        self.buf.append(args)
    def sample(self, batch_size):
        batch = random.sample(self.buf, batch_size)
        return [np.array(x) for x in zip(*batch)]
    def __len__(self):
        return len(self.buf)

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    prices = df['close'].values.astype(float)
    window = training_config.get('window', 20)
    episodes = training_config.get('episodes', 50)
    batch_size = training_config.get('batch_size', 64)
    gamma = training_config.get('gamma', 0.99)
    eps_start, eps_end, eps_decay = 1.0, 0.01, 0.995

    env = TradingEnv(prices, window=window)
    state_dim = window  # returns + position
    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')

    q_net = QNetwork(state_dim, 3).to(device)
    target_net = QNetwork(state_dim, 3).to(device)
    target_net.load_state_dict(q_net.state_dict())
    opt = torch.optim.Adam(q_net.parameters(), lr=training_config.get('lr', 1e-3))
    buf = ReplayBuffer()
    epsilon = eps_start
    episode_rewards = []

    for ep in range(episodes):
        state = env.reset()
        total_reward = 0
        done = False
        while not done:
            if random.random() < epsilon:
                action = random.randint(0, 2)
            else:
                with torch.no_grad():
                    action = q_net(torch.tensor(state).unsqueeze(0).to(device)).argmax(1).item()
            next_state, reward, done = env.step(action)
            buf.push(state, action, reward, next_state, float(done))
            state = next_state
            total_reward += reward

            if len(buf) >= batch_size:
                s, a, r, ns, d = buf.sample(batch_size)
                s_t = torch.tensor(s, dtype=torch.float32).to(device)
                a_t = torch.tensor(a, dtype=torch.long).to(device)
                r_t = torch.tensor(r, dtype=torch.float32).to(device)
                ns_t = torch.tensor(ns, dtype=torch.float32).to(device)
                d_t = torch.tensor(d, dtype=torch.float32).to(device)

                q_vals = q_net(s_t).gather(1, a_t.unsqueeze(1)).squeeze()
                with torch.no_grad():
                    next_q = target_net(ns_t).max(1).values
                target = r_t + gamma * next_q * (1 - d_t)
                loss = nn.functional.mse_loss(q_vals, target)
                opt.zero_grad()
                loss.backward()
                opt.step()

        epsilon = max(eps_end, epsilon * eps_decay)
        if ep % 5 == 0:
            target_net.load_state_dict(q_net.state_dict())
        episode_rewards.append(total_reward)

    result = {
        'model': q_net,
        'metrics': {
            'total_pnl': float(env.pnl),
            'avg_reward_last10': float(np.mean(episode_rewards[-10:])),
            'episodes_trained': episodes,
        },
        'episode_rewards': episode_rewards,
    }
    return result
`,
  },
  {
    id: 'rl_policy_gradient',
    label: 'Policy Gradient (REINFORCE)',
    family: 'RL',
    category: 'Policy-Based',
    framework: 'PYTORCH',
    problemType: 'OTHER',
    description: 'REINFORCE algorithm for continuous portfolio allocation',
    libraries: 'numpy, pandas, torch',
    entrypoint: 'train_model',
    code: `# Policy Gradient (REINFORCE) Trading Agent — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.distributions import Categorical

class PolicyNetwork(nn.Module):
    def __init__(self, state_dim, n_actions=3):
        super().__init__()
        self.net = nn.Sequential(
            nn.Linear(state_dim, 128), nn.ReLU(),
            nn.Linear(128, 64), nn.ReLU(),
            nn.Linear(64, n_actions),
            nn.Softmax(dim=-1),
        )
    def forward(self, x):
        return self.net(x)

class TradingEnv:
    def __init__(self, prices, window=20, commission=0.0002):
        self.prices = prices
        self.window = window
        self.commission = commission
        self.reset()

    def reset(self):
        self.idx = self.window
        self.position = 0
        self.pnl = 0.0
        return self._state()

    def _state(self):
        w = self.prices[self.idx - self.window:self.idx]
        returns = np.diff(w) / w[:-1]
        return np.append(returns, self.position).astype(np.float32)

    def step(self, action):
        new_pos = action - 1
        ret = (self.prices[self.idx] - self.prices[self.idx - 1]) / self.prices[self.idx - 1]
        step_pnl = self.position * ret
        if new_pos != self.position:
            step_pnl -= self.commission
        self.position = new_pos
        self.pnl += step_pnl
        self.idx += 1
        done = self.idx >= len(self.prices) - 1
        return self._state(), step_pnl, done

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    prices = df['close'].values.astype(float)
    window = training_config.get('window', 20)
    episodes = training_config.get('episodes', 80)
    gamma = training_config.get('gamma', 0.99)

    env = TradingEnv(prices, window=window)
    state_dim = window
    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')

    policy = PolicyNetwork(state_dim, 3).to(device)
    opt = torch.optim.Adam(policy.parameters(), lr=training_config.get('lr', 3e-4))
    episode_rewards = []

    for ep in range(episodes):
        state = env.reset()
        log_probs, rewards = [], []
        done = False
        while not done:
            s_t = torch.tensor(state).unsqueeze(0).to(device)
            probs = policy(s_t)
            dist = Categorical(probs)
            action = dist.sample()
            log_probs.append(dist.log_prob(action))
            state, reward, done = env.step(action.item())
            rewards.append(reward)

        # Compute discounted returns
        returns = []
        G = 0
        for r in reversed(rewards):
            G = r + gamma * G
            returns.insert(0, G)
        returns = torch.tensor(returns, dtype=torch.float32).to(device)
        returns = (returns - returns.mean()) / (returns.std() + 1e-8)

        loss = -torch.stack(log_probs) * returns
        opt.zero_grad()
        loss.sum().backward()
        opt.step()
        episode_rewards.append(sum(rewards))

    result = {
        'model': policy,
        'metrics': {
            'total_pnl': float(env.pnl),
            'avg_reward_last10': float(np.mean(episode_rewards[-10:])),
            'episodes_trained': episodes,
        },
        'episode_rewards': episode_rewards,
    }
    return result
`,
  },
  {
    id: 'rl_ppo_trading',
    label: 'PPO Trading Agent',
    family: 'RL',
    category: 'Actor-Critic',
    framework: 'PYTORCH',
    problemType: 'OTHER',
    description: 'Proximal Policy Optimization — stable RL for trading environments',
    libraries: 'numpy, pandas, torch',
    entrypoint: 'train_model',
    code: `# PPO Trading Agent — PyTorch
# Inputs: dataset_records, training_config, runtime_spec, metadata

import numpy as np
import pandas as pd
import torch
import torch.nn as nn
from torch.distributions import Categorical

class ActorCritic(nn.Module):
    def __init__(self, state_dim, n_actions=3):
        super().__init__()
        self.shared = nn.Sequential(nn.Linear(state_dim, 128), nn.ReLU(), nn.Linear(128, 64), nn.ReLU())
        self.actor = nn.Linear(64, n_actions)
        self.critic = nn.Linear(64, 1)

    def forward(self, x):
        h = self.shared(x)
        return nn.functional.softmax(self.actor(h), dim=-1), self.critic(h).squeeze(-1)

class TradingEnv:
    def __init__(self, prices, window=20, commission=0.0002):
        self.prices = prices
        self.window = window
        self.commission = commission
        self.reset()

    def reset(self):
        self.idx = self.window
        self.position = 0
        self.pnl = 0.0
        return self._state()

    def _state(self):
        w = self.prices[self.idx - self.window:self.idx]
        returns = np.diff(w) / w[:-1]
        return np.append(returns, self.position).astype(np.float32)

    def step(self, action):
        new_pos = action - 1
        ret = (self.prices[self.idx] - self.prices[self.idx - 1]) / self.prices[self.idx - 1]
        step_pnl = self.position * ret
        if new_pos != self.position:
            step_pnl -= self.commission
        self.position = new_pos
        self.pnl += step_pnl
        self.idx += 1
        done = self.idx >= len(self.prices) - 1
        return self._state(), step_pnl, done

def train_model(dataset_records, training_config, runtime_spec, metadata):
    df = pd.DataFrame(dataset_records).sort_values('timestamp').reset_index(drop=True)
    prices = df['close'].values.astype(float)
    window = training_config.get('window', 20)
    episodes = training_config.get('episodes', 60)
    gamma = training_config.get('gamma', 0.99)
    clip_eps = training_config.get('clip_eps', 0.2)
    ppo_epochs = training_config.get('ppo_epochs', 4)

    env = TradingEnv(prices, window=window)
    state_dim = window
    device = torch.device('cuda' if torch.cuda.is_available() else 'cpu')

    ac = ActorCritic(state_dim, 3).to(device)
    opt = torch.optim.Adam(ac.parameters(), lr=training_config.get('lr', 3e-4))
    episode_rewards = []

    for ep in range(episodes):
        state = env.reset()
        states, actions, rewards, log_probs_old, values = [], [], [], [], []
        done = False
        while not done:
            s_t = torch.tensor(state).unsqueeze(0).to(device)
            probs, val = ac(s_t)
            dist = Categorical(probs)
            action = dist.sample()
            states.append(state)
            actions.append(action.item())
            log_probs_old.append(dist.log_prob(action).item())
            values.append(val.item())
            state, reward, done = env.step(action.item())
            rewards.append(reward)

        # Compute returns + advantages
        returns, G = [], 0.0
        for r in reversed(rewards):
            G = r + gamma * G
            returns.insert(0, G)
        returns_t = torch.tensor(returns, dtype=torch.float32).to(device)
        values_t = torch.tensor(values, dtype=torch.float32).to(device)
        advantages = returns_t - values_t
        advantages = (advantages - advantages.mean()) / (advantages.std() + 1e-8)

        s_batch = torch.tensor(np.array(states), dtype=torch.float32).to(device)
        a_batch = torch.tensor(actions, dtype=torch.long).to(device)
        old_lp = torch.tensor(log_probs_old, dtype=torch.float32).to(device)

        for _ in range(ppo_epochs):
            probs, vals = ac(s_batch)
            dist = Categorical(probs)
            new_lp = dist.log_prob(a_batch)
            ratio = (new_lp - old_lp).exp()
            surr1 = ratio * advantages
            surr2 = ratio.clamp(1 - clip_eps, 1 + clip_eps) * advantages
            actor_loss = -torch.min(surr1, surr2).mean()
            critic_loss = nn.functional.mse_loss(vals, returns_t)
            loss = actor_loss + 0.5 * critic_loss
            opt.zero_grad()
            loss.backward()
            opt.step()

        episode_rewards.append(sum(rewards))

    result = {
        'model': ac,
        'metrics': {
            'total_pnl': float(env.pnl),
            'avg_reward_last10': float(np.mean(episode_rewards[-10:])),
            'episodes_trained': episodes,
        },
        'episode_rewards': episode_rewards,
    }
    return result
`,
  },
];

// ---------------------------------------------------------------------------
// Assemble families
// ---------------------------------------------------------------------------

export const TEMPLATE_FAMILIES: TemplateFamily[] = [
  {
    family: 'ML',
    label: 'Machine Learning',
    categories: [
      { label: 'Classification', templates: ML_CLASSIFICATION },
      { label: 'Regression', templates: ML_REGRESSION },
      { label: 'Time-Series', templates: ML_TIMESERIES },
      { label: 'Clustering', templates: ML_CLUSTERING },
      { label: 'Anomaly Detection', templates: ML_ANOMALY },
    ],
  },
  {
    family: 'DL',
    label: 'Deep Learning',
    categories: [
      { label: 'Feedforward', templates: DL_FEEDFORWARD },
      { label: 'Sequence Models', templates: DL_SEQUENCE },
      { label: 'Convolutional', templates: DL_CNN },
      { label: 'Autoencoder', templates: DL_AUTOENCODER },
    ],
  },
  {
    family: 'RL',
    label: 'Reinforcement Learning',
    categories: [
      { label: 'Value-Based', templates: RL_TRADING.filter((t) => t.id === 'rl_dqn_trading') },
      { label: 'Policy-Based', templates: RL_TRADING.filter((t) => t.id === 'rl_policy_gradient') },
      { label: 'Actor-Critic', templates: RL_TRADING.filter((t) => t.id === 'rl_ppo_trading') },
    ],
  },
];

export const ALL_TEMPLATES: CodeTemplate[] = TEMPLATE_FAMILIES.flatMap((f) =>
  f.categories.flatMap((c) => c.templates)
);

// ---------------------------------------------------------------------------
// Documentation content — rendered in the Docs tab
// ---------------------------------------------------------------------------

export const RECIPE_DOCS = `
## Recipe Standard Contract

Every custom recipe receives **four inputs** and must return a **result dict**.

### Inputs

| Variable | Type | Description |
|---|---|---|
| \`dataset_records\` | \`list[dict]\` | Raw dataset rows (OHLCV bars, tabular rows, etc.) |
| \`training_config\` | \`dict\` | Training configuration — target column, feature columns, hyper-parameters |
| \`runtime_spec\` | \`dict\` | Saved runtime / resource settings from the asset card |
| \`metadata\` | \`dict\` | Additional asset metadata (author, tags, description) |

### Expected Output

Return a \`dict\` with at least:

\`\`\`python
result = {
    'model': <trained_model>,        # pickle-serialisable model object
    'metrics': {                      # evaluation metrics (numbers)
        'accuracy': 0.93,
        'f1_weighted': 0.91,
    },
    # Optional extras:
    'scaler': <fitted_scaler>,
    'label_encoder': <fitted_encoder>,
    'feature_importances': {...},
}
\`\`\`

### Standard Pattern

1. **Parse & prepare** — Convert \`dataset_records\` to a DataFrame, identify features and target.
2. **Feature engineering** — Create lag features, indicators, or transforms as needed.
3. **Split** — Use \`train_test_split\` for tabular or chronological split for time-series.
4. **Scale** — Fit a \`StandardScaler\` on training data, transform both sets.
5. **Train** — Instantiate and fit the model with hyper-parameters from \`training_config\`.
6. **Evaluate** — Compute metrics on the test set.
7. **Return** — Pack model, scaler, encoder, and metrics into the result dict.

### ML Templates

| Template | Algorithm | Problem Type | Key Params |
|---|---|---|---|
| Random Forest Classifier | \`RandomForestClassifier\` | Classification | n_estimators, max_depth |
| XGBoost Classifier | \`XGBClassifier\` | Classification | n_estimators, learning_rate, max_depth |
| SVM Classifier | \`SVC\` | Classification | C, kernel, gamma |
| Logistic Regression | \`LogisticRegression\` | Classification | C, max_iter |
| Gradient Boosting Regressor | \`GradientBoostingRegressor\` | Regression | n_estimators, learning_rate |
| Ridge Regression | \`Ridge\` | Regression | alpha |
| ElasticNet Regression | \`ElasticNet\` | Regression | alpha, l1_ratio |
| Price Forecast (OHLCV) | \`GradientBoostingRegressor\` | Time-Series Regression | lookback |
| Trading Signal Classifier | \`RandomForestClassifier\` | Time-Series Classification | signal_threshold |
| K-Means Clustering | \`KMeans\` | Clustering | n_clusters |
| Isolation Forest | \`IsolationForest\` | Anomaly Detection | contamination |

### DL Templates

| Template | Architecture | Problem Type | Key Params |
|---|---|---|---|
| MLP Classifier | Feedforward NN | Classification | hidden_dims, epochs, lr |
| MLP Regressor | Feedforward NN | Regression | hidden_dims, epochs, lr |
| LSTM Price Forecast | LSTM | Time-Series Regression | seq_len, hidden_dim, num_layers |
| GRU Sequence Classifier | GRU | Sequential Classification | seq_len, hidden_dim, num_layers |
| Transformer Forecast | Transformer Encoder | Time-Series Regression | seq_len, d_model, nhead |
| 1D-CNN Classifier | Conv1D | Sequential Classification | seq_len, epochs |
| Autoencoder Anomaly | Undercomplete AE | Anomaly Detection | latent_dim, anomaly_percentile |

### RL Templates

| Template | Algorithm | Category | Key Params |
|---|---|---|---|
| DQN Trading Agent | Deep Q-Network | Value-Based | window, episodes, gamma |
| REINFORCE Agent | Policy Gradient | Policy-Based | window, episodes, gamma |
| PPO Trading Agent | Proximal Policy Opt. | Actor-Critic | window, episodes, clip_eps |

### Tips

- **Time-series data**: Always sort by timestamp before splitting. Use chronological split instead of random to avoid look-ahead bias.
- **RL agents**: The minimum data requirement is \`window + episodes × bars_per_episode\`. Start with shorter windows for small datasets.
- **GPU**: Templates auto-detect CUDA. Set executor to \`gpu\` in the asset for GPU-enabled training.
- **Custom hyper-parameters**: All templates read from \`training_config\`. Override any parameter when launching a training job.
`;
